import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Package, Search, X, Filter, ArrowUpDown, Loader2,
  AlertCircle, ChevronLeft, ChevronRight, Warehouse,
  TrendingUp, AlertTriangle, CheckCircle, Calendar, FileText,
  Droplets, Ruler, Box, Weight, Layers, Wind, Circle
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';
import { Upload, Download } from 'lucide-react';

const PAGE_SIZES = [5, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;
const LOW_STOCK_THRESHOLD = 5;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const aggregateInventory = (inventoryData) => {
  const map = new Map();
  inventoryData.forEach((inv) => {
    const key = `${inv.itemCode}`;
    const isReverted = (inv.remarks || '').toLowerCase().includes('reverted from gr');
    if (!map.has(key)) {
      map.set(key, {
        itemCode: inv.itemCode,
        itemDescription: inv.itemDescription,
        uom: inv.uom || '—',
        warehouseCode: [],
        totalQty: 0,
        availableQty: 0,
        reservedQty: 0,
        batchMap: new Map(),
        movements: [],
      });
    }
    const entry = map.get(key);
    const qty = Number(inv.quantity) || 0;
    const availQty = Number(inv.availableQty) || 0;
    entry.totalQty += qty;
    entry.availableQty += availQty;
    entry.reservedQty += Number(inv.reservedQty) || 0;
    if (!entry.warehouseCode.includes(inv.warehouseCode)) {
      entry.warehouseCode.push(inv.warehouseCode);
    }
    const bKey = `${inv.batchNo || 'No Batch'}||${inv.warehouseCode}`;
    if (!entry.batchMap.has(bKey)) {
      entry.batchMap.set(bKey, {
        batchNo: inv.batchNo || 'No Batch',
        warehouseCode: inv.warehouseCode,
        qty: 0,
        expiryDate: inv.expiryDate,
      });
    }
    entry.batchMap.get(bKey).qty += qty;
    entry.movements.push({
      grNo: inv.poNumber || `INV-${inv.itemCode}`,
      grDate: inv.createdAt || new Date().toISOString(),
      receivedQty: qty,
      batchNo: inv.batchNo || '—',
      expiryDate: inv.expiryDate,
      isReverted: isReverted,
    });
  });
  return Array.from(map.values()).map((item) => ({
    ...item,
    totalQty: Math.max(0, item.totalQty),
    availableQty: Math.max(0, item.availableQty),
    warehouseCode: item.warehouseCode.join(' / '),
    batchEntries: Array.from(item.batchMap.values()),
    batchMap: undefined,
  }));
};

const StockBadge = ({ qty, available }) => {
  if (available <= 0)
    return (
      <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-red-200">
        <AlertTriangle className="w-3.5 h-3.5" /> Out of Stock
      </span>
    );
  if (available <= LOW_STOCK_THRESHOLD)
    return (
      <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-amber-200">
        <AlertTriangle className="w-3.5 h-3.5" /> Low Stock
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-emerald-200">
      <CheckCircle className="w-3.5 h-3.5" /> In Stock
    </span>
  );
};

// ─── UOM DATA ───────────────────────────────────────────────────────────────
const UOM_DATA = [
  {
    code: 'ST',
    name: 'Set',
    category: 'Count',
    categoryColor: 'indigo',
    desc: 'Grupo ng items na isang bundle',
    example: 'Tool set, Chair set',
    imgUrl: 'https://img.icons8.com/fluency/96/toolbox.png',
  },
  {
    code: 'PC',
    name: 'Piece',
    category: 'Count',
    categoryColor: 'blue',
    desc: 'Pinaka common — individual item count',
    example: '1 pc laptop, 1 pc chair',
    imgUrl: 'https://img.icons8.com/fluency/96/product.png',
  },
  {
    code: 'LI',
    name: 'Liter',
    category: 'Liquid',
    categoryColor: 'cyan',
    desc: 'Liquid measurement',
    example: '1 liter paint, oil, lubricant',
    imgUrl: 'https://img.icons8.com/fluency/96/water.png',
  },
  {
    code: 'GA',
    name: 'Gallon',
    category: 'Liquid',
    categoryColor: 'teal',
    desc: 'Mas malaking liquid — 1 gal ≈ 3.785L',
    example: 'Paint gallon, fuel gallon',
    imgUrl: 'https://img.icons8.com/fluency/96/water-bottle.png',

  },
  {
    code: 'ME',
    name: 'Meter',
    category: 'Length',
    categoryColor: 'purple',
    desc: 'Haba / length measurement',
    example: 'Wire, cable, tela, hose',
    imgUrl: 'https://img.icons8.com/fluency/96/ruler.png',
  },
  {
    code: 'BX',
    name: 'Box',
    category: 'Pack',
    categoryColor: 'amber',
    desc: 'Naka-pack by box',
    example: 'Box of gloves, box of screws',
    imgUrl: 'https://img.icons8.com/fluency/96/cardboard-box.png',
  },
  {
    code: 'KG',
    name: 'Kilogram',
    category: 'Weight',
    categoryColor: 'orange',
    desc: 'Weight / timbang measurement',
    example: '5kg rice, cement, steel',
    imgUrl: 'https://img.icons8.com/fluency/96/scale.png',
  },
  {
    code: 'PAIL',
    name: 'Pail',
    category: 'Liquid',
    categoryColor: 'sky',
    desc: 'Timba / balde — for paint, chemicals',
    example: '1 pail paint, 1 pail grease',
    imgUrl: 'https://img.icons8.com/fluency/96/paint-bucket.png',
  },
  {
    code: 'RL',
    name: 'Roll',
    category: 'Length',
    categoryColor: 'violet',
    desc: 'Naka-roll na item',
    example: 'Electrical tape, plastic roll, wire roll',
    imgUrl: 'https://img.icons8.com/fluency/96/reel.png',
  },
];

const CATEGORY_COLORS = {
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', badge: 'bg-indigo-100 text-indigo-700 border-indigo-200', code: 'bg-indigo-600 text-white' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700 border-blue-200', code: 'bg-blue-600 text-white' },
  cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', badge: 'bg-cyan-100 text-cyan-700 border-cyan-200', code: 'bg-cyan-600 text-white' },
  teal: { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-100 text-teal-700 border-teal-200', code: 'bg-teal-600 text-white' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700 border-purple-200', code: 'bg-purple-600 text-white' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700 border-amber-200', code: 'bg-amber-600 text-white' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700 border-orange-200', code: 'bg-orange-600 text-white' },
  sky: { bg: 'bg-sky-50', border: 'border-sky-200', badge: 'bg-sky-100 text-sky-700 border-sky-200', code: 'bg-sky-600 text-white' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700 border-violet-200', code: 'bg-violet-600 text-white' },
};

// ─── UOM GUIDE TAB ───────────────────────────────────────────────────────────
function UOMGuideTab() {
  const categories = [...new Set(UOM_DATA.map(u => u.category))];
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? UOM_DATA
    : UOM_DATA.filter(u => u.category === activeCategory);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Ruler className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Unit of Measure (UOM) Reference Guide</h2>
            <p className="text-sm text-gray-500">Complete reference ng lahat ng UOM na ginagamit sa sistema</p>
          </div>
        </div>

        {/* Category filter pills */}
        <div className="flex flex-wrap gap-2 mt-4">
          {['All', ...categories].map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all ${activeCategory === cat
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400 hover:text-indigo-600'
                }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* UOM Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((uom) => {
          const c = CATEGORY_COLORS[uom.categoryColor] || CATEGORY_COLORS.blue;
          return (
            <div
              key={uom.code}
              className={`${c.bg} ${c.border} border rounded-2xl p-5 hover:shadow-lg transition-all hover:-translate-y-0.5 group`}
            >
              {/* Top row: image + code */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-white shadow-sm border border-white flex items-center justify-center overflow-hidden">
                    <img
                      src={uom.imgUrl}
                      alt={uom.name}
                      className="w-10 h-10 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div>
                    <span className={`${c.code} text-lg font-black px-3 py-0.5 rounded-lg font-mono tracking-wider inline-block`}>
                      {uom.code}
                    </span>
                    <p className="text-base font-bold text-gray-800 mt-0.5">{uom.name}</p>
                  </div>
                </div>
                <span className={`${c.badge} border text-xs font-bold px-2 py-0.5 rounded-full`}>
                  {uom.category}
                </span>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-2 leading-relaxed">{uom.desc}</p>

              {/* Example */}
              <div className="bg-white/70 rounded-lg px-3 py-2 border border-white">
                <p className="text-xs text-gray-400 font-semibold uppercase mb-0.5">Example</p>
                <p className="text-xs text-gray-700 font-medium">💡 {uom.example}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">Summary by Category</h3>
        <div className="flex flex-wrap gap-3">
          {categories.map(cat => {
            const items = UOM_DATA.filter(u => u.category === cat);
            const firstItem = items[0];
            const c = CATEGORY_COLORS[firstItem?.categoryColor] || CATEGORY_COLORS.blue;
            return (
              <div key={cat} className={`${c.bg} ${c.border} border rounded-xl px-4 py-3 flex items-center gap-3`}>
                <img src={firstItem?.imgUrl} alt={cat} className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                <div>
                  <p className="text-xs text-gray-500 font-semibold">{cat}</p>
                  <p className="font-bold text-gray-800">{items.length} UOM{items.length > 1 ? 's' : ''}</p>
                  <p className="text-xs text-gray-500">{items.map(i => i.code).join(', ')}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── FUEL MONITOR TAB ────────────────────────────────────────────────────────
// ─── PASTE THIS OVER YOUR EXISTING FuelMonitorTab FUNCTION ───────────────────

function FuelMonitorTab({ inventory }) {
  const FUEL_KEYWORDS = ['fuel', 'diesel', 'gasoline', 'petrol', 'oil', 'lubricant', 'kerosene'];

  const fuelItems = inventory.filter(item =>
    FUEL_KEYWORDS.some(kw =>
      item.itemDescription?.toLowerCase().includes(kw) ||
      item.itemCode?.toLowerCase().includes(kw)
    )
  );

  const totalQty = fuelItems.reduce((s, i) => s + i.totalQty, 0);
  const totalAvail = fuelItems.reduce((s, i) => s + i.availableQty, 0);
  const totalReserved = fuelItems.reduce((s, i) => s + i.reservedQty, 0);

  // ── Fuel Logs state ──────────────────────────────────────────────────────
  const [fuelLogs, setFuelLogs] = React.useState([]);
  const [loadingLogs, setLoadingLogs] = React.useState(false);
  const [submittingLog, setSubmittingLog] = React.useState(false);
  const [expandedDays, setExpandedDays] = React.useState({});
  const [showLogForm, setShowLogForm] = React.useState(false);
  const [logForm, setLogForm] = React.useState({
    plateNumber: '',
    truckDescription: '',
    dipstickReading: '',
    litersRemaining: '',
    tankLocation: 'Main Tank',
    tankCapacity: '',
    shift: 'Morning',
    logDate: new Date().toISOString().split('T')[0],
    remarks: '',
  });

  const TANK_LOCATIONS = ['Main Tank', 'Reserve Tank', 'Tank 1', 'Tank 2', 'Tank 3'];
  const SHIFTS = ['Morning', 'Afternoon', 'Night'];

  // ── Fetch logs ─────────────────────────────────────────────────────────
  const fetchFuelLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/FuelLog`);
      if (res.ok) {
        const data = await res.json();
        setFuelLogs(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to fetch fuel logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  React.useEffect(() => { fetchFuelLogs(); }, []);

  // ── Group logs by day ──────────────────────────────────────────────────
  const groupedByDay = React.useMemo(() => {
    const map = {};
    fuelLogs.forEach(log => {
      const day = log.logDate?.split('T')[0] || new Date(log.dateCreated).toISOString().split('T')[0];
      if (!map[day]) map[day] = [];
      map[day].push(log);
    });
    return Object.entries(map)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, logs]) => ({
        date,
        logs,
        minRemaining: Math.min(...logs.map(l => l.litersRemaining)),
        totalConsumed: logs.reduce((s, l) => s + (l.litersConsumed || 0), 0),
        entryCount: logs.length,
      }));
  }, [fuelLogs]);

  // ── Weekly stats ───────────────────────────────────────────────────────
  const weeklyStats = React.useMemo(() => {
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 6);
    const weekLogs = fuelLogs.filter(l => new Date(l.logDate || l.dateCreated) >= weekAgo);
    return {
      totalConsumed: weekLogs.reduce((s, l) => s + (l.litersConsumed || 0), 0),
      latestReading: fuelLogs[0]?.litersRemaining ?? '—',
    };
  }, [fuelLogs]);

  // ── Submit log ─────────────────────────────────────────────────────────
  const handleSubmitLog = async () => {
    if (!logForm.dipstickReading || !logForm.litersRemaining) {
      Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Dipstick reading and liters remaining are required.', confirmButtonColor: '#2563eb' });
      return;
    }
    setSubmittingLog(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const payload = {
        plateNumber: logForm.plateNumber,
        truckDescription: logForm.truckDescription,
        dipstickReading: parseFloat(logForm.dipstickReading) || 0,
        litersRemaining: parseFloat(logForm.litersRemaining) || 0,
        tankLocation: logForm.tankLocation,
        tankCapacity: logForm.tankCapacity ? parseFloat(logForm.tankCapacity) : null,
        shift: logForm.shift,
        logDate: new Date(logForm.logDate).toISOString(),
        remarks: logForm.remarks,
        loggedBy: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'System',
        loggedByUserId: user.id || user.userId,
      };

      const res = await fetch(`${API_BASE_URL}api/FuelLog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to save fuel log.');

      await Swal.fire({ icon: 'success', title: 'Fuel Log Saved!', timer: 1500, showConfirmButton: false });
      setShowLogForm(false);
      setLogForm({
        plateNumber: '', truckDescription: '', dipstickReading: '',
        litersRemaining: '', tankLocation: 'Main Tank', tankCapacity: '',
        shift: 'Morning', logDate: new Date().toISOString().split('T')[0], remarks: '',
      });
      fetchFuelLogs();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save Failed', text: err.message, confirmButtonColor: '#ef4444' });
    } finally {
      setSubmittingLog(false);
    }
  };

  // ── Delete log ─────────────────────────────────────────────────────────
  const handleDeleteLog = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this log entry?', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280', confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_BASE_URL}api/FuelLog/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      fetchFuelLogs();
    } catch {
      Swal.fire({ icon: 'error', title: 'Delete Failed', confirmButtonColor: '#ef4444' });
    }
  };

  const toggleDay = (date) =>
    setExpandedDays(prev => ({ ...prev, [date]: !prev[date] }));

  // ── Gauge ──────────────────────────────────────────────────────────────
  const FuelGauge = ({ current, max, label }) => {
    const pct = max > 0 ? Math.min(100, (current / max) * 100) : 0;
    const color = pct < 20 ? 'bg-red-500' : pct < 50 ? 'bg-amber-500' : 'bg-green-500';
    return (
      <div>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-500 font-medium">{label}</span>
          <span className={`font-bold ${pct < 20 ? 'text-red-600' : pct < 50 ? 'text-amber-600' : 'text-green-600'}`}>
            {current.toLocaleString()} L
          </span>
        </div>
        <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
        </div>
        <p className="text-right text-xs text-gray-400 mt-0.5">{pct.toFixed(1)}%</p>
      </div>
    );
  };

  // ── Shift badge ────────────────────────────────────────────────────────
  const ShiftBadge = ({ shift }) => {
    const cfg = {
      Morning: 'bg-amber-100 text-amber-700 border-amber-200',
      Afternoon: 'bg-blue-100 text-blue-700 border-blue-200',
      Night: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    };
    const emoji = shift === 'Morning' ? '🌅' : shift === 'Afternoon' ? '☀️' : '🌙';
    return (
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${cfg[shift] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
        {emoji} {shift}
      </span>
    );
  };

  return (
    <div className="space-y-5">

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center">
            <img src="https://img.icons8.com/fluency/96/gas-station.png" alt="fuel" className="w-10 h-10" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-gray-900">⛽ Fuel Monitor</h2>
            <p className="text-sm text-gray-500 mt-0.5">Real-time inventory + dipstick log tracking</p>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <div className="text-right">
              <p className="text-3xl font-black text-gray-900">{fuelItems.length}</p>
              <p className="text-gray-400 text-xs font-semibold">Fuel SKUs</p>
            </div>
            <button
              onClick={() => setShowLogForm(v => !v)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2.5 rounded-xl transition-all shadow-md text-sm"
            >
              <img src="https://img.icons8.com/fluency/24/add.png" alt="add" className="w-4 h-4"
                onError={e => { e.target.style.display = 'none'; }} />
              Log Dipstick
            </button>
          </div>
        </div>
      </div>

      {/* ── LOG FORM ───────────────────────────────────────────────────────── */}
      {showLogForm && (
        <div className="bg-white rounded-2xl border-2 border-blue-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <img src="https://img.icons8.com/fluency/48/clipboard.png" alt="form" className="w-6 h-6"
              onError={e => { e.target.style.display = 'none'; }} />
            <h3 className="text-gray-900 font-bold text-base">New Dipstick / Fuel Log Entry</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Log Date</label>
              <input type="date" value={logForm.logDate}
                onChange={e => setLogForm(p => ({ ...p, logDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Shift</label>
              <select value={logForm.shift}
                onChange={e => setLogForm(p => ({ ...p, shift: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition bg-white"
              >
                {SHIFTS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Tank Location</label>
              <select value={logForm.tankLocation}
                onChange={e => setLogForm(p => ({ ...p, tankLocation: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition bg-white"
              >
                {TANK_LOCATIONS.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>


            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">
                Tank Capacity (L) <span className="text-gray-400 normal-case font-normal">(optional)</span>
              </label>
              <input type="number" placeholder="e.g. 200" value={logForm.tankCapacity}
                onChange={e => setLogForm(p => ({ ...p, tankCapacity: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">
                Dipstick Reading <span className="text-gray-400 normal-case font-normal">(cm)</span>
              </label>
              <input type="number" step="0.01" placeholder="e.g. 45.5" value={logForm.dipstickReading}
                onChange={e => setLogForm(p => ({ ...p, dipstickReading: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">
                Liters Remaining <span className="text-blue-600">*</span>
              </label>
              <input type="number" step="0.01" placeholder="e.g. 150.00" value={logForm.litersRemaining}
                onChange={e => setLogForm(p => ({ ...p, litersRemaining: e.target.value }))}
                className="w-full border-2 border-blue-400 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-200 transition font-bold"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Remarks</label>
              <input type="text" placeholder="Optional notes..." value={logForm.remarks}
                onChange={e => setLogForm(p => ({ ...p, remarks: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-5 pt-5 border-t border-gray-100">
            <button onClick={() => setShowLogForm(false)}
              className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-semibold transition">
              Cancel
            </button>
            <button onClick={handleSubmitLog} disabled={submittingLog}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition shadow-md">
              {submittingLog ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
              ) : (
                <><img src="https://img.icons8.com/fluency/24/save.png" alt="save" className="w-4 h-4"
                  onError={e => { e.target.style.display = 'none'; }} /> Save Log</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── SUMMARY CARDS ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total On Hand', value: `${totalAvail} L`, icon: 'https://img.icons8.com/fluency/96/fuel-pump.png', bg: 'from-blue-50 to-blue-100', border: 'border-blue-200', val: 'text-blue-700' },
          { label: 'Available Stock', value: `${totalQty} L`, icon: 'https://img.icons8.com/fluency/96/ok--v1.png', bg: 'from-green-50 to-green-100', border: 'border-green-200', val: 'text-green-700' },
          { label: 'Latest Reading', value: `${weeklyStats.latestReading} L`, icon: 'https://img.icons8.com/fluency/96/ruler.png', bg: 'from-indigo-50 to-indigo-100', border: 'border-indigo-200', val: 'text-indigo-700' },
          { label: 'Weekly Consumed', value: `${weeklyStats.totalConsumed} L`, icon: 'https://img.icons8.com/fluency/96/high-priority.png', bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', val: 'text-amber-700' },
        ].map((card, i) => (
          <div key={i} className={`bg-gradient-to-br ${card.bg} border ${card.border} rounded-xl p-4 shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">{card.label}</span>
              <img src={card.icon} alt={card.label} className="w-9 h-9 object-contain"
                onError={e => { e.target.style.display = 'none'; }} />
            </div>
            <p className={`text-2xl font-black ${card.val}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* ── FUEL GAUGES ────────────────────────────────────────────────────── */}
      {fuelItems.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
            <img src="https://img.icons8.com/fluency/48/speedometer.png" alt="gauge" className="w-5 h-5"
              onError={e => { e.target.style.display = 'none'; }} />
            Fuel Level Gauges
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fuelItems.map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <img src="https://img.icons8.com/fluency/48/gas-station.png" alt="fuel" className="w-7 h-7"
                      onError={e => { e.target.style.display = 'none'; }} />
                    <div>
                      <p className="font-bold text-gray-800 text-sm">{item.itemDescription}</p>
                      <p className="text-xs text-gray-500 font-mono">{item.itemCode} · {item.warehouseCode}</p>
                    </div>
                  </div>
                  <StockBadge qty={item.availableQty} available={item.totalQty} />
                </div>
                <FuelGauge label="Available / Total" current={item.totalQty} max={item.availableQty} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── FUEL LOG TABLE — per day collapsible ───────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <img src="https://img.icons8.com/fluency/48/activity-feed.png" alt="log" className="w-5 h-5"
              onError={e => { e.target.style.display = 'none'; }} />
            <h3 className="text-gray-800 font-bold text-sm">Dipstick / Fuel Log</h3>
            <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full border border-blue-200">
              {fuelLogs.length} entries
            </span>
          </div>
          <button onClick={fetchFuelLogs} disabled={loadingLogs}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 hover:border-gray-400 px-3 py-1.5 rounded-lg transition">
            <img src="https://img.icons8.com/fluency/24/synchronize.png" alt="refresh" className="w-3.5 h-3.5"
              onError={e => { e.target.style.display = 'none'; }} />
            Refresh
          </button>
        </div>

        {loadingLogs ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading fuel logs...
          </div>
        ) : groupedByDay.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <img src="https://img.icons8.com/fluency/96/fuel-pump.png" alt="empty" className="w-14 h-14 mx-auto mb-3 opacity-40"
              onError={e => { e.target.style.display = 'none'; }} />
            <p className="font-semibold text-gray-500">No fuel logs yet</p>
            <p className="text-xs mt-1">Click "Log Dipstick" to add your first entry</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {groupedByDay.map(({ date, logs, minRemaining, totalConsumed, entryCount }) => {
              const isExpanded = expandedDays[date];
              const dateLabel = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
              });
              const isToday = date === new Date().toISOString().split('T')[0];

              return (
                <div key={date}>

                  {/* ── Day collapsed row ── */}
                  <button
                    onClick={() => toggleDay(date)}
                    className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-blue-50 transition text-left group"
                  >
                    <ChevronRight className={`w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />

                    <div className="flex items-center gap-2 min-w-[180px]">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-bold text-gray-800">{dateLabel}</span>
                      {isToday && (
                        <span className="bg-blue-600 text-white text-xs font-black px-2 py-0.5 rounded-full">TODAY</span>
                      )}
                    </div>

                    <div className="flex items-center gap-8 ml-auto">
                      <div className="text-center">
                        <p className="text-xs text-gray-400 font-semibold">Entries</p>
                        <p className="text-sm font-bold text-gray-700">{entryCount}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-400 font-semibold">Consumed</p>
                        <p className="text-sm font-bold text-amber-600">{totalConsumed.toFixed(1)} L</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-400 font-semibold">Min Remaining</p>
                        <p className={`text-sm font-bold ${minRemaining < 50 ? 'text-red-600' : 'text-green-600'}`}>
                          {minRemaining.toFixed(1)} L
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* ── Expanded detail rows ── */}
                  {isExpanded && (
                    <div className="border-t border-blue-100 bg-blue-50/30">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-blue-50 border-b border-blue-100">
                            {['#', 'Shift', 'Tank', 'Dipstick', 'Liters Remaining', 'Consumed', 'Logged By', 'Remarks', ''].map(h => (
                              <th key={h} className="px-4 py-2.5 text-left text-xs font-bold text-blue-700 uppercase tracking-wide whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map((log, idx) => (
                            <tr key={log.id} className="border-b border-gray-100 hover:bg-white transition-colors">
                              <td className="px-4 py-3 text-gray-400 text-xs">{idx + 1}</td>
                              <td className="px-4 py-3"><ShiftBadge shift={log.shift} /></td>
                              <td className="px-4 py-3">
                                <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                                  {log.tankLocation || '—'}
                                </span>
                              </td>

                              <td className="px-4 py-3">
                                <span className="font-bold text-gray-700">{log.dipstickReading}</span>
                                <span className="text-gray-400 text-xs ml-1">cm</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`font-black text-base ${log.litersRemaining < 50 ? 'text-red-600' : 'text-green-600'}`}>
                                  {Number(log.litersRemaining).toFixed(1)}
                                </span>
                                <span className="text-gray-400 text-xs ml-1">L</span>
                                {log.tankCapacity && (
                                  <div className="mt-1 w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${log.litersRemaining / log.tankCapacity < 0.2 ? 'bg-red-500' : 'bg-green-500'}`}
                                      style={{ width: `${Math.min(100, (log.litersRemaining / log.tankCapacity) * 100)}%` }}
                                    />
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {log.litersConsumed != null && log.litersConsumed > 0
                                  ? <span className="text-amber-600 font-bold text-sm">-{Number(log.litersConsumed).toFixed(1)} L</span>
                                  : <span className="text-gray-300 text-xs">—</span>}
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">{log.loggedBy || '—'}</td>
                              <td className="px-4 py-3 text-xs text-gray-400 max-w-[120px] truncate">{log.remarks || '—'}</td>
                              <td className="px-4 py-3">
                                <button onClick={() => handleDeleteLog(log.id)}
                                  className="text-gray-300 hover:text-red-500 transition" title="Delete">
                                  <img src="https://img.icons8.com/fluency/24/trash.png" alt="delete" className="w-4 h-4"
                                    onError={e => { e.target.style.display = 'none'; }} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {/* ── Day footer totals ── */}
                        <tfoot>
                          <tr className="border-t-2 border-blue-200 bg-blue-50">
                            <td colSpan={4} className="px-4 py-2.5 text-xs font-bold text-blue-700 uppercase">Day Total</td>
                            <td className="px-4 py-2.5"></td>
                            <td className="px-4 py-2.5">
                              <span className="text-xs font-bold text-green-600">Min: {minRemaining.toFixed(1)} L</span>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="text-xs font-bold text-amber-600">{totalConsumed.toFixed(1)} L consumed</span>
                            </td>
                            <td colSpan={3}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── INVENTORY DETAIL TABLE ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="https://img.icons8.com/fluency/48/gas-station.png" alt="fuel" className="w-5 h-5"
              onError={e => { e.target.style.display = 'none'; }} />
            <h3 className="text-gray-800 font-bold text-sm">Fuel Inventory Details</h3>
          </div>
          <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full border border-blue-200">
            {fuelItems.length} items
          </span>
        </div>

        {fuelItems.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <img src="https://img.icons8.com/fluency/96/fuel-pump.png" alt="no fuel" className="w-14 h-14 mx-auto mb-3 opacity-40"
              onError={e => { e.target.style.display = 'none'; }} />
            <p className="font-semibold text-gray-500">No fuel items found</p>
            <p className="text-xs mt-1 text-gray-400">Items with "fuel", "diesel", "gasoline", or "oil" will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['#', 'Item Code', 'Description', 'UOM', 'Warehouse', 'Total Qty', 'Available', 'Reserved', 'Batches', 'Status'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fuelItems.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                    <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono font-bold text-gray-800 bg-blue-100 px-2 py-0.5 rounded text-xs">{item.itemCode}</span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-700">{item.itemDescription}</td>
                    <td className="px-4 py-3">
                      <span className="bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded text-xs font-bold">{item.uom || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-semibold border border-slate-200">
                        🏭 {item.warehouseCode}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold text-gray-900">{item.availableQty.toLocaleString()}</td>
                    <td className="px-4 py-3 font-bold text-green-600">{item.totalQty.toLocaleString()}</td>
                    <td className="px-4 py-3 font-bold text-amber-600">{item.reservedQty.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {item.batchEntries.slice(0, 2).map((b, bi) => (
                          <span key={bi} className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-xs border border-gray-200">
                            {b.batchNo} ({b.qty})
                          </span>
                        ))}
                        {item.batchEntries.length > 2 && (
                          <span className="text-xs text-gray-400">+{item.batchEntries.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StockBadge qty={item.availableQty} available={item.totalQty} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-blue-50 border-t-2 border-blue-200">
                  <td colSpan={5} className="px-4 py-3 text-xs font-bold text-blue-700 uppercase">TOTAL</td>
                  <td className="px-4 py-3 font-black text-blue-700">{totalAvail.toLocaleString()}</td>
                  <td className="px-4 py-3 font-black text-green-700">{totalQty.toLocaleString()}</td>
                  <td className="px-4 py-3 font-black text-amber-700">{totalReserved.toLocaleString()}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function InventoryList() {
  const [activeTab, setActiveTab] = useState('inventory');
  const [showImportModal, setShowImportModal] = useState(false);

  const [grHeaders, setGrHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncInfo, setSyncInfo] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('');
  const [filterStock, setFilterStock] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortKey, setSortKey] = useState('itemCode');
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedItem, setSelectedItem] = useState(null);
  const [allMRs, setAllMRs] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [returnHistoryForItem, setReturnHistoryForItem] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [loadingMRHistory, setLoadingMRHistory] = useState(false);
  const [mrHistoryForItem, setMrHistoryForItem] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) { try { setCurrentUser(JSON.parse(userData)); } catch { } }
  }, []);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true); setError(null);
    try {
      const invRes = await fetch(`${API_BASE_URL}api/InventoryData`);
      if (!invRes.ok) throw new Error(`Failed to fetch inventory: ${invRes.status}`);
      const invData = await invRes.json();
      setGrHeaders(Array.isArray(invData) ? invData : []);
      try {
        const mrRes = await fetch(`${API_BASE_URL}api/MRHeader`);
        if (mrRes.ok) {
          const mrData = await mrRes.json();
          const mrArray = Array.isArray(mrData) ? mrData : [];
          const mrWithDetails = await Promise.all(
            mrArray.map(async (mr) => {
              try {
                const detRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mr.id}`);
                if (detRes.ok) {
                  const details = await detRes.json();
                  return { ...mr, mrDetails: Array.isArray(details) ? details : [] };
                }
                return { ...mr, mrDetails: [] };
              } catch { return { ...mr, mrDetails: [] }; }
            })
          );
          setAllMRs(mrWithDetails);
        }
      } catch (err) { setAllMRs([]); }
    } catch (err) { setError(err.message || 'Failed to load data'); }
    finally { setLoading(false); }
  };

  const fetchMRHistoryForItem = (item) => {
    setLoadingMRHistory(true);
    const matched = allMRs.filter(mr => (mr.mrDetails || []).some(d => d.itemCode === item.itemCode));
    setMrHistoryForItem(matched);
    setLoadingMRHistory(false);
  };

  const fetchReturnHistory = async (item) => {
    setLoadingReturns(true); setReturnHistoryForItem([]);
    try {
      const res = await fetch(`${API_BASE_URL}api/ReturnRequest`);
      if (res.ok) {
        const allReturns = await res.json();
        setReturnHistoryForItem(allReturns.filter(r => r.itemCode === item.itemCode));
      }
    } catch { setReturnHistoryForItem([]); }
    finally { setLoadingReturns(false); }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: 'Delete Inventory Record?',
      html: `Are you sure you want to delete <strong>${item.itemCode}</strong>?<br><small class="text-red-600">This will delete ALL batches for this item!</small>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#dc2626', cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!', cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;
    setDeletingId(`${item.itemCode}-${item.warehouseCode}`);
    try {
      const fetchResponse = await fetch(`${API_BASE_URL}api/InventoryData?itemCode=${item.itemCode}`);
      if (!fetchResponse.ok) throw new Error('Failed to fetch inventory records');
      const inventoryRecords = await fetchResponse.json();
      if (!inventoryRecords || inventoryRecords.length === 0) throw new Error('No inventory records found');
      let deletedCount = 0, errorCount = 0;
      for (const record of inventoryRecords) {
        try {
          const r = await fetch(`${API_BASE_URL}api/InventoryData/${record.id}`, { method: 'DELETE' });
          if (r.ok) deletedCount++; else errorCount++;
        } catch { errorCount++; }
      }
      if (deletedCount > 0) {
        const user = JSON.parse(localStorage.getItem("user") || '{}');
        await fetch(`${API_BASE_URL}api/Logs`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "DELETE", module: "Inventory", description: `Deleted Item: ${item.itemCode}. ${deletedCount} record(s) deleted.`, userId: user?.userId, username: user?.username, fullName: user?.fullName || user?.username })
        }).catch(() => { });
        Swal.fire({ icon: 'success', title: 'Deleted!', html: `Deleted ${deletedCount} record(s).`, timer: 3000, showConfirmButton: false });
        fetchData();
      } else throw new Error('Failed to delete any inventory records');
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: error.message, confirmButtonColor: '#dc2626' });
    } finally { setDeletingId(null); }
  };

  const inventory = useMemo(() => aggregateInventory(grHeaders), [grHeaders]);
  const warehouseOptions = useMemo(() => [...new Set(inventory.map((i) => i.warehouseCode).filter(Boolean))].sort(), [inventory]);

  const filtered = useMemo(() => {
    let data = [...inventory];
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter((i) => i.itemCode?.toLowerCase().includes(q) || i.itemDescription?.toLowerCase().includes(q) || i.warehouseCode?.toLowerCase().includes(q));
    }
    if (filterWarehouse) data = data.filter((i) => i.warehouseCode === filterWarehouse);
    if (filterStock === 'low') data = data.filter((i) => i.totalQty > 0 && i.totalQty <= LOW_STOCK_THRESHOLD);
    else if (filterStock === 'out') data = data.filter((i) => i.totalQty <= 0);
    data.sort((a, b) => {
      let valA = a[sortKey], valB = b[sortKey];
      if (sortKey === 'totalQty') { valA = Number(valA); valB = Number(valB); }
      else { valA = (valA || '').toString().toLowerCase(); valB = (valB || '').toString().toLowerCase(); }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [inventory, searchText, filterWarehouse, filterStock, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);
  useEffect(() => { setCurrentPage(1); }, [searchText, filterWarehouse, filterStock, pageSize]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3.5 h-3.5 opacity-30" />;
    return <span className="inline-flex text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const totalItems = inventory.length;
  const totalUnits = inventory.reduce((s, i) => s + i.totalQty, 0);
  const lowCount = inventory.filter((i) => i.totalQty > 0 && i.totalQty <= LOW_STOCK_THRESHOLD).length;
  const outCount = inventory.filter((i) => i.totalQty <= 0).length;
  const hasActiveFilters = searchText.trim() || filterWarehouse || filterStock;
  const clearFilters = () => { setSearchText(''); setFilterWarehouse(''); setFilterStock(''); };

  const statusConfig = {
    0: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-200' },
    1: { label: 'Pending', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    2: { label: 'Approved', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    3: { label: 'Complete', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    '-1': { label: 'Rejected', color: 'bg-red-50 text-red-700 border-red-200' },
  };

  // ── TAB DEFINITIONS ────────────────────────────────────────────────────────
  const TABS = [
    {
      key: 'inventory',
      label: 'Inventory',
      imgUrl: 'https://img.icons8.com/fluency/48/warehouse.png',
    },
    {
      key: 'uom',
      label: 'UOM Guide',
      imgUrl: 'https://img.icons8.com/fluency/48/ruler.png',
    },
    {
      key: 'fuel',
      label: 'Fuel Monitor',
      imgUrl: 'https://img.icons8.com/fluency/48/fuel-pump.png',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-8xl mx-auto">

        {syncStatus && (
          <div className={`flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-lg mb-4 border ${syncStatus === 'syncing' ? 'bg-slate-50 border-slate-300 text-slate-700' : syncStatus === 'done' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-red-50 border-red-300 text-red-800'}`}>
            {syncStatus === 'syncing' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {syncStatus === 'done' && <CheckCircle className="w-3.5 h-3.5" />}
            <span>{syncStatus === 'syncing' ? 'Posting to InventoryData…' : `InventoryData synced — ${syncInfo}`}</span>
            {syncStatus === 'done' && <button onClick={() => setSyncStatus(null)} className="ml-auto opacity-50 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>}
          </div>
        )}

        {/* ── PAGE HEADER ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src="https://img.icons8.com/fluency/64/warehouse.png" alt="inventory" className="w-10 h-10" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Inventory / Current Stock</h1>
                <p className="text-sm text-gray-500 mt-0.5">Aggregated from all Goods Receipts</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={fetchData} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition text-gray-600" title="Refresh">
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              {activeTab === 'inventory' && (
                <>
                  <button
                    onClick={() => exportInventoryToCSV(inventory)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100 text-sm font-semibold transition"
                    title="Export to CSV"
                  >
                    <Download className="w-4 h-4" /> Export
                  </button>
                  <button
                    onClick={() => setShowImportModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-400 bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-semibold transition shadow-sm"
                    title="Import CSV"
                  >
                    <Upload className="w-4 h-4" /> Import
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── TAB BUTTONS ── */}
        <div className="flex gap-1 mb-5 bg-white rounded-xl shadow-sm border border-gray-200 p-1.5">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.key
                ? 'bg-indigo-600 text-white shadow-md'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
            >
              <img
                src={tab.imgUrl}
                alt={tab.label}
                className="w-5 h-5 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── INVENTORY TAB ── */}
        {activeTab === 'inventory' && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              {[
                { label: 'Total SKUs', value: totalItems, imgUrl: 'https://img.icons8.com/fluency/64/product.png', color: 'slate', sub: 'unique items' },
                { label: 'Total Units', value: totalUnits, imgUrl: 'https://img.icons8.com/fluency/64/stacked-boxes.png', color: 'teal', sub: 'across all warehouses' },
                { label: 'Low Stock', value: lowCount, imgUrl: 'https://img.icons8.com/fluency/64/low-battery.png', color: 'amber', sub: `≤ ${LOW_STOCK_THRESHOLD} units` },
                { label: 'Out of Stock', value: outCount, imgUrl: 'https://img.icons8.com/fluency/64/empty-box.png', color: 'red', sub: '0 units remaining' },
              ].map((card, i) => {
                const colors = {
                  slate: { bg: 'bg-slate-50', border: 'border-slate-200', val: 'text-slate-900' },
                  teal: { bg: 'bg-teal-50', border: 'border-teal-200', val: 'text-teal-900' },
                  amber: { bg: 'bg-amber-50', border: 'border-amber-200', val: 'text-amber-900' },
                  red: { bg: 'bg-red-50', border: 'border-red-200', val: 'text-red-900' },
                };
                const c = colors[card.color];
                return (
                  <div key={i} className={`${c.bg} border ${c.border} rounded-xl p-4 shadow-sm`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-600 font-semibold uppercase tracking-wide">{card.label}</span>
                      <img src={card.imgUrl} alt={card.label} className="w-8 h-8 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                    <p className={`text-2xl font-bold ${c.val}`}>{card.value}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{card.sub}</p>
                  </div>
                );
              })}
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" placeholder="Search Item Code / Description…" value={searchText} onChange={(e) => setSearchText(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400" />
                  </div>
                  <select value={filterWarehouse} onChange={(e) => setFilterWarehouse(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white text-gray-700">
                    <option value="">All Warehouses</option>
                    {warehouseOptions.map((w) => <option key={w} value={w}>{w}</option>)}
                  </select>
                  <select value={filterStock} onChange={(e) => setFilterStock(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white text-gray-700">
                    <option value="">All Stock Status</option>
                    <option value="low">Low Stock (≤ {LOW_STOCK_THRESHOLD})</option>
                    <option value="out">Out of Stock</option>
                  </select>
                </div>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="mt-3 text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1">
                    <X className="w-3 h-3" /> Clear all filters
                  </button>
                )}
              </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {loading && (
                <div className="flex flex-col items-center justify-center py-24">
                  <Loader2 className="w-10 h-10 text-slate-500 animate-spin mb-3" />
                  <p className="text-gray-500 text-sm">Loading inventory…</p>
                </div>
              )}
              {!loading && error && (
                <div className="flex flex-col items-center justify-center py-24">
                  <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
                  <p className="text-red-600 font-semibold">{error}</p>
                  <button onClick={fetchData} className="mt-2 text-sm text-slate-600 underline">Retry</button>
                </div>
              )}
              {!loading && !error && filtered.length === 0 && (
                <div className="flex flex-col items-center justify-center py-24">
                  <img src="https://img.icons8.com/fluency/96/empty-box.png" alt="empty" className="w-16 h-16 mb-3 opacity-50" />
                  <p className="text-gray-500 font-medium">{hasActiveFilters ? 'No items match your filters.' : 'No inventory yet.'}</p>
                  {hasActiveFilters && <button onClick={clearFilters} className="mt-2 text-sm text-slate-600 underline">Clear filters</button>}
                </div>
              )}
              {!loading && !error && filtered.length > 0 && (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider w-10">#</th>
                          {[
                            { key: 'itemCode', label: 'Item Code' },
                            { key: 'itemDescription', label: 'Description' },
                            { key: 'warehouseCode', label: 'Warehouse' },
                            { key: 'totalQty', label: 'Qty On Hand' },
                          ].map((col) => (
                            <th key={col.key} onClick={() => handleSort(col.key)}
                              className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer select-none hover:text-slate-900 transition">
                              <span className="inline-flex items-center gap-1.5">{col.label}<SortIcon col={col.key} /></span>
                            </th>
                          ))}
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">UOM</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Batches</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Movements</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.map((item, idx) => {
                          const rowNum = (safeCurrentPage - 1) * pageSize + idx + 1;
                          return (
                            <tr key={`${item.itemCode}-${item.warehouseCode}`} className="border-b border-gray-100 hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-3 text-gray-400 font-medium">{rowNum}</td>
                              <td className="px-4 py-3"><span className="font-mono font-semibold text-slate-800">{item.itemCode}</span></td>
                              <td className="px-4 py-3 text-gray-700 max-w-[180px] truncate">{item.itemDescription}</td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md text-xs font-semibold border border-slate-200">
                                  <Warehouse className="w-3 h-3" />{item.warehouseCode}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Total:</span>
                                    <span className="text-lg font-bold text-gray-900">{item.availableQty}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Available:</span>
                                    <span className={`text-lg font-bold ${item.totalQty <= 0 ? 'text-red-600' : 'text-green-600'}`}>{item.totalQty}</span>
                                  </div>
                                  {item.reservedQty > 0 && (
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-amber-600">Reserved:</span>
                                      <span className="text-sm font-semibold text-amber-700">{item.reservedQty}</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {/* UOM with icon lookup */}
                                {(() => {
                                  const uomInfo = UOM_DATA.find(u => u.code === (item.uom || '').toUpperCase());
                                  return (
                                    <div className="flex items-center gap-1.5">
                                      {uomInfo ? (
                                        <img src={uomInfo.imgUrl} alt={uomInfo.code} className="w-5 h-5 object-contain"
                                          onError={(e) => { e.target.style.display = 'none'; }} />
                                      ) : null}
                                      <span className="bg-indigo-100 text-indigo-800 border border-indigo-200 px-2 py-0.5 rounded text-xs font-bold font-mono">
                                        {item.uom || '—'}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1">
                                  {item.batchEntries.slice(0, 3).map((b, bi) => (
                                    <span key={bi} className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-semibold border border-slate-200">
                                      {b.batchNo} <span className="text-slate-500">({b.qty})</span>
                                    </span>
                                  ))}
                                  {item.batchEntries.length > 3 && <span className="text-xs text-gray-400">+{item.batchEntries.length - 3} more</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3"><StockBadge qty={item.totalQty} available={item.availableQty} /></td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md text-xs font-semibold border border-gray-200">
                                  <FileText className="w-3 h-3" />{item.movements.length} GR{item.movements.length !== 1 ? 's' : ''}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => { setSelectedItem(item); fetchMRHistoryForItem(item); fetchReturnHistory(item); }}
                                    className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition">
                                    <img src="https://img.icons8.com/fluency/24/visible.png" alt="view" className="w-3.5 h-3.5"
                                      onError={(e) => { e.target.style.display = 'none'; }} />
                                    Details
                                  </button>
                                  {(currentUser?.positionId === 'SYSTEM ADMIN' || currentUser?.position === 'SYSTEM ADMIN' || currentUser?.role === 'SYSTEM ADMIN') && (
                                    <button
                                      onClick={() => handleDelete(item)}
                                      disabled={deletingId === `${item.itemCode}-${item.warehouseCode}`}
                                      className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition disabled:opacity-50">
                                      {deletingId === `${item.itemCode}-${item.warehouseCode}` ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      ) : (
                                        <img src="https://img.icons8.com/fluency/24/trash.png" alt="delete" className="w-3.5 h-3.5"
                                          onError={(e) => { e.target.style.display = 'none'; }} />
                                      )}
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
{showImportModal && (
  <ImportInventoryModal
    inventory={inventory}
    onClose={() => setShowImportModal(false)}
    onImportDone={() => { setShowImportModal(false); fetchData(); }}
  />
)}
                  {/* Pagination */}
                  <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50 gap-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Rows per page:</span>
                      <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                        className="border border-gray-300 rounded-lg px-2 py-1 bg-white text-sm focus:outline-none">
                        {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <span className="text-gray-400">({(safeCurrentPage - 1) * pageSize + 1}–{Math.min(safeCurrentPage * pageSize, filtered.length)} of {filtered.length})</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button disabled={safeCurrentPage === 1} onClick={() => setCurrentPage(1)}
                        className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 hover:bg-gray-100 transition">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                      </button>
                      <button disabled={safeCurrentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 hover:bg-gray-100 transition">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      {(() => {
                        const pages = []; const range = 2;
                        let start = Math.max(1, safeCurrentPage - range);
                        let end = Math.min(totalPages, safeCurrentPage + range);
                        if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
                        for (let i = start; i <= end; i++) pages.push(i);
                        if (end < totalPages) { if (end < totalPages - 1) pages.push('...'); pages.push(totalPages); }
                        return pages.map((p, i) =>
                          p === '...' ? <span key={`e${i}`} className="px-1 text-gray-400 text-sm">…</span> : (
                            <button key={p} onClick={() => setCurrentPage(p)}
                              className={`w-8 h-8 rounded-md text-sm font-semibold transition ${p === safeCurrentPage ? 'bg-slate-700 text-white shadow-sm' : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-100'}`}>{p}</button>
                          )
                        );
                      })()}
                      <button disabled={safeCurrentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 hover:bg-gray-100 transition">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button disabled={safeCurrentPage === totalPages} onClick={() => setCurrentPage(totalPages)}
                        className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 hover:bg-gray-100 transition">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* ── UOM GUIDE TAB ── */}
        {activeTab === 'uom' && <UOMGuideTab />}

        {/* ── FUEL MONITOR TAB ── */}
        {activeTab === 'fuel' && <FuelMonitorTab inventory={inventory} />}

      </div>

      {/* ═══ DETAIL MODAL ═══ */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-indigo-700 to-indigo-600 text-white px-6 py-4 flex-shrink-0 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="https://img.icons8.com/fluency/48/product.png" alt="item" className="w-8 h-8"
                  onError={(e) => { e.target.style.display = 'none'; }} />
                <div>
                  <h3 className="text-lg font-bold font-mono">{selectedItem.itemCode}</h3>
                  <p className="text-slate-300 text-xs">{selectedItem.itemDescription}</p>
                </div>
              </div>
              <button onClick={() => setSelectedItem(null)} className="bg-white/10 hover:bg-white/20 rounded-lg p-1.5 transition"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-teal-700 font-semibold uppercase mb-1">Total On Hand</p>
                  <p className="text-2xl font-bold text-teal-900">{selectedItem.totalQty}</p>
                  <p className="text-xs text-teal-500">units</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-700 font-semibold uppercase mb-1">Batches</p>
                  <p className="text-2xl font-bold text-slate-900">{selectedItem.batchEntries.length}</p>
                  <p className="text-xs text-slate-500">tracked</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-700 font-semibold uppercase mb-1">Warehouse</p>
                  <p className="text-lg font-bold text-slate-900">{selectedItem.warehouseCode}</p>
                  <p className="text-xs text-slate-500">location</p>
                </div>
              </div>

              {/* Batch Breakdown */}
              <div className="mb-5">
                <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <img src="https://img.icons8.com/fluency/32/package.png" alt="batch" className="w-4 h-4" /> Batch Breakdown
                </h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        {['#', 'Batch / Lot No', 'Warehouse', 'Qty', 'Expiry Date', 'Status'].map(h => (
                          <th key={h} className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItem.batchEntries.map((b, i) => {
                        const isExpired = b.expiryDate && new Date(b.expiryDate) < new Date();
                        const isNearExpiry = b.expiryDate && !isExpired && (new Date(b.expiryDate) - new Date()) < 30 * 24 * 60 * 60 * 1000;
                        return (
                          <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                            <td className="px-4 py-2 font-semibold text-gray-800">{b.batchNo}</td>
                            <td className="px-4 py-2 text-xs text-gray-600">{b.warehouseCode || '—'}</td>
                            <td className="px-4 py-2 text-right font-bold text-teal-700">{b.qty}</td>
                            <td className="px-4 py-2 text-gray-600 text-xs">{formatDate(b.expiryDate)}</td>
                            <td className="px-4 py-2">
                              {isExpired ? <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-xs font-semibold border border-red-200">Expired</span>
                                : isNearExpiry ? <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded text-xs font-semibold border border-amber-200">Near Expiry</span>
                                  : b.expiryDate ? <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-xs font-semibold border border-emerald-200">Valid</span>
                                    : <span className="text-gray-400 text-xs">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Stock Movement History */}
              <div className="mb-5">
                <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <img src="https://img.icons8.com/fluency/32/activity-feed.png" alt="movement" className="w-4 h-4" />
                  Stock Movement History
                  <span className="bg-slate-100 text-slate-700 text-xs font-bold px-2 py-0.5 rounded-full border border-slate-200">{selectedItem.movements.length} entries</span>
                </h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        {['#', 'GR No', 'GR Date', 'Qty Received', 'Batch', 'Expiry'].map(h => (
                          <th key={h} className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItem.movements.map((m, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                          <td className="px-4 py-2"><span className="font-mono font-semibold text-slate-800 text-xs">{m.grNo}</span></td>
                          <td className="px-4 py-2 text-gray-600 text-xs flex items-center gap-1"><Calendar className="w-3 h-3 text-gray-400" />{formatDate(m.grDate)}</td>
                          <td className="px-4 py-2 text-right font-bold">
                            {m.isReverted ? <span className="text-red-600">-{m.receivedQty}</span> : <span className="text-teal-700">+{m.receivedQty}</span>}
                          </td>
                          <td className="px-4 py-2">{m.batchNo !== '—' ? <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-semibold border border-slate-200">{m.batchNo}</span> : <span className="text-gray-400 text-xs">—</span>}</td>
                          <td className="px-4 py-2 text-gray-500 text-xs">{formatDate(m.expiryDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* MR Usage History */}
              <div className="mb-5">
                <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <img src="https://img.icons8.com/fluency/32/document.png" alt="mr" className="w-4 h-4" />
                  Material Request Usage History
                  <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full border border-blue-200">
                    {loadingMRHistory ? '...' : `${mrHistoryForItem.length} MRs`}
                  </span>
                </h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {loadingMRHistory ? (
                    <div className="flex items-center justify-center gap-2 py-8 text-gray-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />Loading MR history...
                    </div>
                  ) : mrHistoryForItem.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      <img src="https://img.icons8.com/fluency/64/empty-box.png" alt="empty" className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No Material Requests yet for this item</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-blue-50">
                          {['#', 'Control No', 'Date', 'Requested By', 'Department', 'Warehouse', 'Qty Requested', 'Status'].map(h => (
                            <th key={h} className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-blue-700 uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {mrHistoryForItem.map((mr, i) => {
                          const detail = (mr.mrDetails || []).find(d => d.itemCode === selectedItem.itemCode);
                          const sc = statusConfig[String(mr.status)] || statusConfig[0];
                          return (
                            <tr key={mr.id || i} className="border-b border-gray-100 last:border-0 hover:bg-blue-50 transition-colors">
                              <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                              <td className="px-4 py-2"><span className="font-mono font-semibold text-blue-800 text-xs">{mr.controlNo || mr.mrNo}</span></td>
                              <td className="px-4 py-2 text-gray-600 text-xs"><span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-gray-400" />{formatDate(mr.mrDate)}</span></td>
                              <td className="px-4 py-2 text-gray-700 text-xs">{mr.requestedByName || '—'}</td>
                              <td className="px-4 py-2 text-gray-700 text-xs">{mr.department || mr.departmentName || '—'}</td>
                              <td className="px-4 py-2 text-gray-600 text-xs">{detail?.warehouseCode || '—'}</td>
                              <td className="px-4 py-2 text-right"><span className="font-bold text-red-600">-{detail?.requestedQty || 0}</span></td>
                              <td className="px-4 py-2 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${sc.color}`}>{sc.label}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Return History */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <img src="https://img.icons8.com/fluency/32/return-purchase.png" alt="return" className="w-4 h-4" />
                  Return History
                  <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full border border-orange-200">{returnHistoryForItem.length} Returns</span>
                </h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {loadingReturns ? (
                    <div className="flex items-center justify-center gap-2 py-8 text-gray-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />Loading return history...
                    </div>
                  ) : returnHistoryForItem.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      <p className="text-sm">No return history for this item</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-orange-50">
                          {['#', 'Date', 'MR Control No', 'Returned By', 'Return Qty', 'Reason', 'Status'].map(h => (
                            <th key={h} className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-orange-700 uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {returnHistoryForItem.map((ret, i) => (
                          <tr key={ret.id || i} className="border-b border-gray-100 last:border-0 hover:bg-orange-50 transition-colors">
                            <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                            <td className="px-4 py-2 text-gray-600 text-xs">{formatDate(ret.requestedDate)}</td>
                            <td className="px-4 py-2"><span className="font-mono font-semibold text-blue-800 text-xs">{ret.mrControlNo || ret.mrHeaderId || '—'}</span></td>
                            <td className="px-4 py-2 text-gray-700 text-xs">{ret.requestedByName || '—'}</td>
                            <td className="px-4 py-2 text-right font-bold text-green-600">+{ret.requestedReturnQty}</td>
                            <td className="px-4 py-2 text-gray-600 text-xs max-w-[150px] truncate">{ret.returnReason || '—'}</td>
                            <td className="px-4 py-2 text-center">
                              {ret.status === 1
                                ? <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-xs font-bold border border-green-200">Approved</span>
                                : ret.status === -1
                                  ? <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold border border-red-200">Rejected</span>
                                  : <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full text-xs font-bold border border-amber-200">Pending</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <button onClick={() => setSelectedItem(null)} className="text-sm text-gray-500 hover:text-gray-700 font-medium transition">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── EXPORT HELPER ───────────────────────────────────────────────────────────
function exportInventoryToCSV(inventory) {
  const headers = [
    'Item Code', 'Description', 'Warehouse', 'UOM',
    'Total Qty', 'Available Qty', 'Reserved Qty',
    'Batches', 'Batch Details'
  ];

  const rows = inventory.map(item => [
    item.itemCode,
    item.itemDescription,
    item.warehouseCode,
    item.uom || '',
    item.totalQty,
    item.availableQty,
    item.reservedQty,
    item.batchEntries.length,
    item.batchEntries.map(b => `${b.batchNo}(${b.qty})`).join('|'),
  ]);

  const csvContent = [headers, ...rows]
    .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `inventory_export_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── IMPORT MODAL ─────────────────────────────────────────────────────────────
function ImportInventoryModal({ inventory, onClose, onImportDone }) {
  const [step, setStep] = React.useState('upload');
  const [parsedRows, setParsedRows] = React.useState([]);
  const [duplicates, setDuplicates] = React.useState([]);
  const [newRows, setNewRows] = React.useState([]);
  const [duplicateActions, setDuplicateActions] = React.useState({});
  const [importing, setImporting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const fileRef = React.useRef();

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        Swal.fire({ icon: 'warning', title: 'Empty File', text: 'CSV has no data rows.', confirmButtonColor: '#2563eb' });
        return;
      }
      const parseCSVLine = (line) => {
        const result = [];
        let inQuote = false, cur = '';
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') { inQuote = !inQuote; }
          else if (ch === ',' && !inQuote) { result.push(cur.trim()); cur = ''; }
          else { cur += ch; }
        }
        result.push(cur.trim());
        return result;
      };
      const headers = parseCSVLine(lines[0]).map(h => h.replace(/^"|"$/g, '').trim().toLowerCase());
      const rows = lines.slice(1).map((line, i) => {
        const vals = parseCSVLine(line).map(v => v.replace(/^"|"$/g, '').trim());
        const obj = {};
        headers.forEach((h, hi) => { obj[h] = vals[hi] || ''; });
        obj._rowIndex = i;
        return obj;
      }).filter(r => r['item code']);
      const existingKeys = new Set(inventory.map(inv => `${inv.itemCode}||${inv.warehouseCode}`));
      const dups = [], fresh = [];
      rows.forEach(row => {
        const key = `${row['item code']}||${row['warehouse']}`;
        if (existingKeys.has(key)) dups.push(row);
        else fresh.push(row);
      });
      setParsedRows(rows);
      setDuplicates(dups);
      setNewRows(fresh);
      const defaultActions = {};
      dups.forEach((_, i) => { defaultActions[i] = 'skip'; });
      setDuplicateActions(defaultActions);
      setStep('review');
    };
    reader.readAsText(file);
  };

  const setAllDuplicateAction = (action) => {
    const actions = {};
    duplicates.forEach((_, i) => { actions[i] = action; });
    setDuplicateActions(actions);
  };

  const handleImport = async () => {
    setImporting(true);
    let skipped = 0, updated = 0, added = 0, errors = 0;
    const toUpdate = duplicates.filter((_, i) => duplicateActions[i] === 'update');
    const toSkip = duplicates.filter((_, i) => duplicateActions[i] === 'skip');
    skipped = toSkip.length;
    for (const row of newRows) {
      try {
        const payload = {
          itemCode: row['item code'],
          itemDescription: row['description'],
          warehouseCode: row['warehouse'],
          uom: row['uom'],
          quantity: parseFloat(row['total qty']) || 0,
          availableQty: parseFloat(row['available qty']) || 0,
          reservedQty: parseFloat(row['reserved qty']) || 0,
          batchNo: '',
          createdAt: new Date().toISOString(),
          remarks: 'Imported via CSV',
        };
        const res = await fetch(`${API_BASE_URL}api/InventoryData`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) added++; else errors++;
      } catch { errors++; }
    }
    for (const row of toUpdate) {
      try {
        const existing = await fetch(`${API_BASE_URL}api/InventoryData?itemCode=${row['item code']}`);
        if (existing.ok) {
          const records = await existing.json();
          if (records && records.length > 0) {
            const record = records[0];
            const payload = {
              ...record,
              quantity: parseFloat(row['total qty']) || record.quantity,
              availableQty: parseFloat(row['available qty']) || record.availableQty,
              reservedQty: parseFloat(row['reserved qty']) || record.reservedQty,
              uom: row['uom'] || record.uom,
              itemDescription: row['description'] || record.itemDescription,
              remarks: 'Updated via CSV Import',
            };
            const upRes = await fetch(`${API_BASE_URL}api/InventoryData/${record.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            if (upRes.ok) updated++; else errors++;
          }
        }
      } catch { errors++; }
    }
    setResult({ added, updated, skipped, errors });
    setStep('done');
    setImporting(false);
    onImportDone();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-emerald-700 to-emerald-600 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/15 rounded-xl flex items-center justify-center">
              <Upload className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold">Import Inventory</h3>
              <p className="text-emerald-200 text-xs">
                {step === 'upload' ? 'Upload a CSV file to import inventory data'
                  : step === 'review' ? `${parsedRows.length} rows parsed — review before importing`
                  : 'Import complete'}
              </p>
            </div>
          </div>
          {/* Step pills */}
          <div className="flex items-center gap-1.5 mr-3">
            {[
              { key: 'upload', label: '1' },
              { key: 'review', label: '2' },
              { key: 'done',   label: '3' },
            ].map((s, i, arr) => {
              const stepOrder = ['upload', 'review', 'done'];
              const currentIdx = stepOrder.indexOf(step);
              const isActive = step === s.key;
              const isPast = currentIdx > i;
              return (
                <React.Fragment key={s.key}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all
                    ${isActive ? 'bg-white text-emerald-700'
                      : isPast  ? 'bg-white/30 text-white'
                      : 'bg-white/10 text-emerald-300'}`}>
                    {isPast ? <CheckCircle className="w-3.5 h-3.5" /> : s.label}
                  </div>
                  {i < arr.length - 1 && (
                    <div className={`w-5 h-px ${isPast ? 'bg-white/50' : 'bg-white/15'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <button onClick={onClose} className="bg-white/15 hover:bg-white/25 rounded-lg p-1.5 transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">

          {/* ── STEP 1: UPLOAD ── */}
          {step === 'upload' && (
            <div className="space-y-5">
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-emerald-200 rounded-2xl p-12 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50 transition-all group"
              >
                <div className="w-14 h-14 bg-emerald-100 group-hover:bg-emerald-200 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all">
                  <Upload className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-gray-700 font-bold text-sm">Click to browse CSV file</p>
                <p className="text-gray-400 text-xs mt-1">Only .csv files are supported</p>
                <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} className="hidden" />
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Expected CSV Columns</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead>
                      <tr>
                        {['Item Code','Description','Warehouse','UOM','Total Qty','Available Qty','Reserved Qty','Batches','Batch Details'].map(h => (
                          <th key={h} className="px-2 py-2 text-left font-semibold text-gray-500 border border-gray-200 bg-white whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {['GS1438','FUEL DIESEL','WH-02','LI','8000000','7999950','0','1','No Batch(7999950)'].map((v, i) => (
                          <td key={i} className="px-2 py-2 text-gray-400 border border-gray-100 whitespace-nowrap">{v}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-400 mt-2.5 flex items-center gap-1.5">
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  Tip: Export first to get the correct format, then edit and re-import.
                </p>
              </div>
            </div>
          )}

          {/* ── STEP 2: REVIEW ── */}
          {step === 'review' && (
            <div className="space-y-5">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-emerald-700">{newRows.length}</p>
                  <p className="text-xs font-semibold text-emerald-600 uppercase mt-0.5">New Items</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-amber-700">{duplicates.length}</p>
                  <p className="text-xs font-semibold text-amber-600 uppercase mt-0.5">Duplicates</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-slate-700">{parsedRows.length}</p>
                  <p className="text-xs font-semibold text-slate-500 uppercase mt-0.5">Total Rows</p>
                </div>
              </div>

              {/* Duplicates */}
              {duplicates.length > 0 && (
                <div className="border border-amber-200 rounded-xl overflow-hidden">
                  <div className="bg-amber-50 px-4 py-3 flex items-center justify-between border-b border-amber-200">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-bold text-amber-800">Duplicate Items</span>
                      <span className="text-xs text-amber-500">choose action per row</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-400 mr-0.5">All:</span>
                      <button
                        onClick={() => setAllDuplicateAction('skip')}
                        className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
                      >Skip All</button>
                      <button
                        onClick={() => setAllDuplicateAction('update')}
                        className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition"
                      >Update All</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-amber-50 border-b border-amber-100">
                          {['Item Code','Description','Warehouse','CSV Qty','Current Qty','Action'].map(h => (
                            <th key={h} className="px-3 py-2.5 text-left text-xs font-bold text-amber-700 uppercase whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {duplicates.map((row, i) => {
                          const existing = inventory.find(inv =>
                            inv.itemCode === row['item code'] && inv.warehouseCode === row['warehouse']
                          );
                          const action = duplicateActions[i] || 'skip';
                          return (
                            <tr key={i} className={`border-b border-gray-100 transition-colors ${action === 'skip' ? 'opacity-45 bg-gray-50' : 'bg-blue-50/40'}`}>
                              <td className="px-3 py-2.5">
                                <span className="font-mono font-bold text-gray-800 text-xs">{row['item code']}</span>
                              </td>
                              <td className="px-3 py-2.5 text-gray-600 text-xs max-w-[140px] truncate">{row['description']}</td>
                              <td className="px-3 py-2.5">
                                <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs font-semibold border border-slate-200">{row['warehouse']}</span>
                              </td>
                              <td className="px-3 py-2.5 font-bold text-blue-700">{row['total qty']}</td>
                              <td className="px-3 py-2.5 font-medium text-gray-400">{existing?.totalQty ?? '—'}</td>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => setDuplicateActions(prev => ({ ...prev, [i]: 'skip' }))}
                                    className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-all ${action === 'skip'
                                      ? 'bg-gray-600 text-white border-gray-600'
                                      : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'}`}
                                  >Skip</button>
                                  <button
                                    onClick={() => setDuplicateActions(prev => ({ ...prev, [i]: 'update' }))}
                                    className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-all ${action === 'update'
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : 'bg-white text-blue-500 border-blue-200 hover:border-blue-400'}`}
                                  >Update</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* New rows */}
              {newRows.length > 0 && (
                <div className="border border-emerald-200 rounded-xl overflow-hidden">
                  <div className="bg-emerald-50 px-4 py-3 flex items-center gap-2 border-b border-emerald-200">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm font-bold text-emerald-800">New Items</span>
                    <span className="text-xs text-emerald-500">— will be added</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-emerald-50 border-b border-emerald-100">
                          {['Item Code','Description','Warehouse','UOM','Qty'].map(h => (
                            <th key={h} className="px-3 py-2.5 text-left text-xs font-bold text-emerald-700 uppercase whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {newRows.map((row, i) => (
                          <tr key={i} className="border-b border-gray-100 hover:bg-emerald-50/50 transition-colors">
                            <td className="px-3 py-2.5"><span className="font-mono font-bold text-gray-800 text-xs">{row['item code']}</span></td>
                            <td className="px-3 py-2.5 text-gray-600 text-xs">{row['description']}</td>
                            <td className="px-3 py-2.5"><span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-xs font-semibold border border-slate-200">{row['warehouse']}</span></td>
                            <td className="px-3 py-2.5"><span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-xs font-bold border border-indigo-200 font-mono">{row['uom']}</span></td>
                            <td className="px-3 py-2.5 font-bold text-emerald-700">{row['total qty']}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: DONE ── */}
          {step === 'done' && result && (
            <div className="text-center py-10">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-1">Import Complete!</h3>
              <p className="text-gray-400 text-sm mb-7">Here's a summary of what was processed</p>
              <div className="grid grid-cols-4 gap-3 max-w-sm mx-auto">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-emerald-700">{result.added}</p>
                  <p className="text-xs font-semibold text-emerald-600 uppercase">Added</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-blue-700">{result.updated}</p>
                  <p className="text-xs font-semibold text-blue-600 uppercase">Updated</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-gray-500">{result.skipped}</p>
                  <p className="text-xs font-semibold text-gray-400 uppercase">Skipped</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                  <p className="text-2xl font-black text-red-600">{result.errors}</p>
                  <p className="text-xs font-semibold text-red-500 uppercase">Errors</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-6 py-3.5 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-600 font-medium transition">
            {step === 'done' ? 'Close' : 'Cancel'}
          </button>
          {step === 'review' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setStep('upload')}
                className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-100 transition font-semibold"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={handleImport}
                disabled={importing || parsedRows.length === 0}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold px-5 py-2 rounded-xl text-sm transition shadow-sm"
              >
                {importing
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing...</>
                  : <><Upload className="w-4 h-4" /> Confirm Import</>}
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}